import { z } from 'zod';
import { ComponentKindSchema, CriticalitySchema } from '../domain/application.js';
import { CloudNodeTypeSchema, DiscoverySchema } from './cloud-graph.js';
import { IdSchema, IsoDateTimeSchema, UserIdSchema, VersionSchema } from './common.js';
/**
 * Applications proposed from a discovered cloud account (ADR 0008).
 *
 * Discovery finds the account's public entry points — DNS names that resolve to a resource in the
 * account, and public resources with no name of their own — and groups them by evidence: a shared
 * front door, an application tag, a CloudFormation stack. Each group is *proposed* as an
 * application, with components and a draft scope derived from the resources behind it.
 *
 * A proposal is evidence, not a verdict, and never a permission. Accepting one creates an
 * ordinary application; its draft scope is only ever a pre-filled form, which still needs a
 * window, an authorisation and an approver before anything is scanned (ADR 0005 §5).
 *
 * Proposals are computed from the stored, hash-pinned graph on every read — nothing is stored but
 * the decisions people make about them. Names, tags and hosts came from the customer account, so
 * all of it is untrusted text: displayed, never interpreted.
 */
export const PROPOSAL_MAX_HOSTS = 20;
export const PROPOSAL_MAX_RESOURCES = 200;
export const PROPOSALS_MAX = 100;
export const PROPOSAL_ACCEPT_MAX = 10;
/** A short, stable identifier for a proposal: a hash of its sorted hosts. New hosts, new key. */
export const ProposalKeySchema = z.string().regex(/^[0-9a-f]{16}$/, 'Not a proposal key');
/**
 * Why entry points were grouped, or a proposal named, the way it was — strongest first.
 *
 * `APPLICATION_TAG` — a resource carries the `awsApplication` tag or an application/project tag.
 * `STACK` — the entry points lead to resources in the same CloudFormation stack.
 * `FRONT_DOOR` — the entry points route to the same distribution, load balancer or API.
 * `HOST` — nothing stronger: one entry point on its own.
 */
export const ProposalEvidenceKindSchema = z.enum([
    'APPLICATION_TAG',
    'STACK',
    'FRONT_DOOR',
    'HOST',
]);
export const ProposalEvidenceSchema = z.object({
    kind: ProposalEvidenceKindSchema,
    /** The tag (`key=value`), stack, resource or host the evidence is about. */
    detail: z.string().max(600),
});
/** A component as discovery would declare it: the shape the application editor takes. */
export const ProposedComponentSchema = z.object({
    /** Stable within the proposal, so `connectsTo` can name it before it has a real id. */
    ref: z.string().max(40),
    name: z.string().max(80),
    kind: ComponentKindSchema,
    hosts: z.array(z.string().max(253)).max(10),
    connectsTo: z.array(z.string().max(40)).max(40),
    /** The resources this component was derived from. */
    nodeIds: z.array(z.string().max(2048)).max(20),
});
export const ProposedResourceSchema = z.object({
    nodeId: z.string().max(2048),
    type: CloudNodeTypeSchema,
    name: z.string().max(512),
    public: z.boolean(),
});
/**
 * Where a proposal stands against what the organisation already has.
 *
 * `NEW` — none of its hosts belongs to an application: surface nobody owns.
 * `PARTIAL` — some of its hosts belong to an application, some to none.
 * `COVERED` — every host already belongs to an application.
 * `DISMISSED` — someone decided it is not an application; shown again only if its hosts change.
 */
export const ProposalStatusSchema = z.enum(['NEW', 'PARTIAL', 'COVERED', 'DISMISSED']);
export const ApplicationProposalSchema = z.object({
    key: ProposalKeySchema,
    name: z.string().max(120),
    /** Where the name came from, so a reader knows how far to trust it. */
    nameSource: z.enum(['TAG', 'STACK', 'HOST']),
    /** 0–10, from the strongest evidence that grouped it. */
    confidence: z.number().int().min(0).max(10),
    /** Every host a scan could start from, DNS names first. */
    hosts: z.array(z.string().max(253)).max(PROPOSAL_MAX_HOSTS),
    /** True when the only hosts are AWS-assigned names (no DNS name points at it). */
    awsNamesOnly: z.boolean(),
    resources: z.array(ProposedResourceSchema).max(PROPOSAL_MAX_RESOURCES),
    components: z.array(ProposedComponentSchema).max(40),
    evidence: z.array(ProposalEvidenceSchema).max(20),
    /** A pre-filled scope form — never a scope. A person adds the window and authorisation. */
    draftScope: z.object({
        allowedHosts: z.array(z.string().max(253)).max(PROPOSAL_MAX_HOSTS),
        seedUrls: z.array(z.string().max(2048)).max(PROPOSAL_MAX_HOSTS),
    }),
    status: ProposalStatusSchema,
    /** Applications that already claim some of its hosts. */
    coveredBy: z.array(z.object({ applicationId: IdSchema, name: z.string().max(120) })).max(20),
    /** Hosts no application claims — the shadow surface in this proposal. */
    unclaimedHosts: z.array(z.string().max(253)).max(PROPOSAL_MAX_HOSTS),
});
export const DismissedProposalSchema = z.object({
    key: ProposalKeySchema,
    byUserId: UserIdSchema,
    at: IsoDateTimeSchema,
});
/** GET /v1/cloud-connections/{id}/proposals */
export const ListApplicationProposalsResponseSchema = z.object({
    /** The discovery the proposals were computed from; null when none has finished. */
    discovery: DiscoverySchema.nullable(),
    integrity: z.enum(['verified', 'changed']).nullable(),
    proposals: z.array(ApplicationProposalSchema).max(PROPOSALS_MAX),
    summary: z.object({
        new: z.number().int().nonnegative(),
        partial: z.number().int().nonnegative(),
        covered: z.number().int().nonnegative(),
        dismissed: z.number().int().nonnegative(),
        /** Public hosts in the account that no application claims. */
        unclaimedHosts: z.number().int().nonnegative(),
        /** More proposals were found than are returned. */
        truncated: z.boolean(),
    }),
});
/**
 * POST /v1/cloud-connections/{id}/proposals/accept
 *
 * Accepts one proposal, or several as one application. The server recomputes the proposals from
 * the named discovery and builds the application from them — a client cannot supply components.
 */
export const AcceptProposalsRequestSchema = z.strictObject({
    discoveryId: IdSchema,
    keys: z
        .array(ProposalKeySchema)
        .min(1)
        .max(PROPOSAL_ACCEPT_MAX)
        .refine((k) => new Set(k).size === k.length, 'Keys contain duplicates'),
    /** Defaults to the first proposal's name. */
    name: z.string().trim().min(1).max(120).optional(),
    criticality: CriticalitySchema.default('MEDIUM'),
});
/** POST /v1/cloud-connections/{id}/proposals/dismiss */
export const DismissProposalRequestSchema = z.strictObject({
    key: ProposalKeySchema,
    /** The connection's version, so two people dismissing at once cannot lose each other's. */
    expectedVersion: VersionSchema,
});
//# sourceMappingURL=application-proposal.js.map